import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const GRADIENT = 'linear-gradient(277deg, #41F7A2 0%, #5C3DFA 50%, #41F7A2 100%)';

interface LoadingSceneContentsOverlayProps {
  onDismiss?: () => void;
}

const LoadingSceneContentsOverlay = ({ onDismiss }: LoadingSceneContentsOverlayProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  const FilmStripSet = ({ groupKey }: { groupKey: string }) => (
    <div className="flex flex-col justify-between items-center h-[150px] shrink-0 py-[1px]">
      {Array.from({ length: 37 }).map((_, index) => (
        <div
        // biome-ignore lint/suspicious/noArrayIndexKey: key is used for animation
          key={`${groupKey}-${index}`}
          className="w-[8px] h-[2px] bg-black rounded-[4px] shrink-0"
        />
      ))}
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 z-[1001] bg-[rgba(0,0,0,0.30)] touch-none overscroll-none cursor-pointer"
        style={{ backdropFilter: 'blur(4.949999809265137px)' }}
        onClick={onDismiss}
        onKeyDown={(e) => e.key === 'Escape' && onDismiss?.()}
        role="button"
        tabIndex={0}
        aria-label="닫기"
      />
      <div className="fixed inset-0 z-[1002] flex items-center justify-center pointer-events-none">
        <div className="pointer-events-auto flex flex-col items-center">
          <style>{`
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes marquee-up {
          from { transform: translate3d(0, 0, 0); }
          to { transform: translate3d(0, -150px, 0); }
        }
        @keyframes marquee-down {
          from { transform: translate3d(0, -150px, 0); }
          to { transform: translate3d(0, 0, 0); }
        }
      `}</style>
          <div
            className="relative w-[244px] h-[150px] overflow-hidden"
            style={{
              background:
                'linear-gradient(47deg, rgba(69,220,174,0.20) 0%, rgba(93,62,250,0.20) 99.08%)',
              maskImage:
                'linear-gradient(180deg, transparent 2.65%, black 48.18%, transparent 93.71%)',
              WebkitMaskImage:
                'linear-gradient(180deg, transparent 2.65%, black 48.18%, transparent 93.71%)',
              maskSize: '100% 100%',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
            }}
          >
            <div
              className="absolute left-0 top-0 bottom-0 w-[12px]"
              style={{
                backgroundImage: 'linear-gradient(to bottom, #45DCAE 0%, #5D3EFA 50%, #45DCAE 100%)',
              }}
            >
              <div 
                className="flex flex-col"
                style={{ 
                  animation: 'marquee-up 4s linear infinite',
                  willChange: 'transform'
                }}
              >
                <FilmStripSet groupKey="left-1" />
                <FilmStripSet groupKey="left-2" />
              </div>
            </div>

            <div
              className="absolute right-0 top-0 bottom-0 w-[12px]"
              style={{
                backgroundImage: 'linear-gradient(202deg, #5D3EFA 0%, #45DCAE 50%, #5D3EFA 100%)',
              }}
            >
              <div 
                className="flex flex-col"
                style={{ 
                  animation: 'marquee-down 4s linear infinite',
                  willChange: 'transform'
                }}
              >
                <FilmStripSet groupKey="right-1" />
                <FilmStripSet groupKey="right-2" />
              </div>
            </div>

            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[88px] h-[88px]"
              role="img"
              aria-label="Scene Logo"
              style={{
                maskImage: `url('/assets/scene/scene_logo.png')`,
                WebkitMaskImage: `url('/assets/scene/scene_logo.png')`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundImage: GRADIENT,
                backgroundSize: '200% 100%',
                animation: 'gradient-flow 3s linear infinite'
              }}
            />
          </div>
          <p className="text-[20px] font-bold text-pure-white leading-[24px] mt-[19.5px]">
            {t('SceneMain.loadingSceneTitle')}
          </p>
          <p className="text-[15px] font-medium text-pure-white leading-[18px] mt-[14px]">
            {t('SceneMain.loadingSceneDescription')}
          </p>
        </div>
      </div>
    </>
  );
};

export default LoadingSceneContentsOverlay;